import { useRef, useState, useEffect } from "react";
import { Camera, Upload, X } from "lucide-react";
import { toast } from "sonner";
import ConsentGate from "@/components/ConsentGate";
import { useI18n } from "@/context/I18nContext";

// Shared capture surface for all three FluentLens modes: live camera OR device upload,
// mobile-first, with graceful fallback to Upload when the camera is denied/unavailable.
export const LensCapture = ({ onCapture, loading, hint }) => {
  const { t } = useI18n();
  const [stream, setStream] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => { if (stream) stream.getTracks().forEach((t) => t.stop()); }, [stream]);

  const stopCam = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null); setCamOn(false);
  };

  const startCam = async () => {
    if (!localStorage.getItem("fluent_consent_camera")) { setShowConsent(true); return; }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setStream(s); setCamOn(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); } }, 100);
    } catch {
      toast.error("No camera available — upload a photo instead.");
      fileRef.current?.click();
    }
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const b64 = canvas.toDataURL("image/jpeg", 0.8).split(",")[1];
    stopCam();
    onCapture(b64);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result.split(",")[1]);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="nb-card p-4 md:p-6" data-testid="lens-capture">
      <div className="relative rounded-2xl border-4 border-dashed border-ink overflow-hidden bg-paper aspect-[4/3] flex items-center justify-center">
        {camOn ? (
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
        ) : loading ? (
          <div className="text-center">
            <div className="w-14 h-14 border-4 border-ink border-t-coral rounded-full animate-spin mx-auto" />
            <p className="font-cabinet font-bold mt-4">Reading…</p>
          </div>
        ) : (
          <div className="text-center px-6">
            <Camera className="w-12 h-12 mx-auto stroke-[2]" />
            <p className="font-cabinet font-bold text-lg mt-3">{hint || "Point at text or an object"}</p>
            <p className="text-sm text-gray-500">{t("lensDesc.captureHelp")}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-4 mt-5">
        {camOn ? (
          <>
            <button onClick={stopCam} className="nb-btn-outline w-14 h-14 rounded-full p-0"><X className="w-6 h-6" /></button>
            <button onClick={snap} data-testid="lens-snap-btn" className="w-20 h-20 bg-coral border-4 border-ink rounded-full shadow-hard hover:shadow-hard-sm transition-all flex items-center justify-center">
              <Camera className="w-8 h-8 text-white stroke-[2.5]" />
            </button>
            <div className="w-14" />
          </>
        ) : (
          <>
            <button onClick={startCam} disabled={loading} data-testid="lens-camera-btn" className="nb-btn-primary px-6 py-4"><Camera className="w-5 h-5" /> {t("lens.takePhoto")}</button>
            <button onClick={() => fileRef.current?.click()} disabled={loading} data-testid="lens-upload-btn" className="nb-btn-secondary px-6 py-4"><Upload className="w-5 h-5" /> {t("lens.uploadImage")}</button>
          </>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="lens-file-input" />
      </div>
      <ConsentGate type="camera" open={showConsent} onGrant={() => { setShowConsent(false); startCam(); }} onClose={() => setShowConsent(false)} />
    </div>
  );
};

// Small reusable vocab-adder (reuses the existing dedup-safe /vocab/add endpoint).
export const VocabAdder = ({ words, source = "fluentlens", example = "" }) => {
  return { words, source, example };
};
